import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { ApiGatewayService } from '@wam/authentication';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { Product } from '@wam/shared';
import { WATERFORD_STUDENT } from './waterford-apps.model';
import { WithAuthService } from './services/with-auth.service';
import {
  AssignmentBatchItem,
  AssignmentBatchItemResponse,
  AssignmentResponse,
  AssignmentStatus,
  AssignmentTimes,
  DocumentType,
  LicenseError,
  Method,
} from '@wap/catalog-v5';
import { isEmpty } from 'lodash-es';

export class FailuresError extends Error {
  failures: { isLicenseError?: boolean; message: string }[];

  constructor(failures: { isLicenseError?: boolean; message: string }[]) {
    super('Could not perform assignment for some students');
    this.failures = failures;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AssignmentsService extends WithAuthService {
  private apiGatewayService = inject(ApiGatewayService);

  getCourses(organization: string, uuid: string): Observable<AssignmentResponse[]> {
    return this.getAssignments(organization, uuid).pipe(
      take(1),
      map((assignments) =>
        assignments.filter(
          (a) =>
            a.document?.documentType === DocumentType.COURSE || a.document.product === Product.ayla,
        ),
      ),
    );
  }

  getAssignedDocumentsFor(uuid: string): Observable<AssignmentResponse[]> {
    return this.withUser().pipe(
      mergeMap((user) => {
        // @ts-ignore
        return this.getAssignments(user.organization, uuid, [
          AssignmentStatus.ACTIVE,
          AssignmentStatus.INACTIVE,
        ]).pipe(
          map((assignments) => {
            // @ts-ignore
            return assignments.filter((a) => a.document.documentType !== DocumentType.COURSE) as [];
          }),
        );
      }),
    );
  }

  removeAssignment(assignment: AssignmentResponse, app = WATERFORD_STUDENT): Observable<unknown> {
    return this.apiGatewayService.delete(
      `catalog-v5/v5` +
        `/apps/${app}` +
        `/orgs/${assignment.organization}` +
        `/users/${assignment.user}` +
        `/assignments/${assignment.id}`,
    );
  }

  completeAssignment(documentId: string, timestamp: string): Observable<AssignmentResponse> {
    return this.withUser().pipe(
      mergeMap((user) => {
        return this.apiGatewayService
          .patch<AssignmentResponse>(
            `catalog-v5/v5/apps/${WATERFORD_STUDENT}/orgs/${user.organization}/users/${user.uuid}/documents/${documentId}/assignments`,
            {
              status: AssignmentStatus.COMPLETED,
              ...(timestamp ? { metadata: { timestamp: +timestamp } } : {}),
            },
            'assignments',
          )
          .pipe(
            catchError((error) => {
              if (error.status === 404) {
                return of({} as AssignmentResponse);
              }
              return throwError(() => error);
            }),
          );
      }),
    );
  }

  assignmentCreateUpdateDelete(
    assignmentsToCreateOrUpdate: AssignmentResponse[],
    assignmentsToDelete: AssignmentResponse[],
    schoolId: string | null = null,
    throwError: boolean = true,
  ): Observable<{
    failures?: AssignmentBatchItemResponse[];
    successes: AssignmentResponse[];
  }> {
    // @ts-ignore
    const createOrUpdateItems: AssignmentBatchItem[] = assignmentsToCreateOrUpdate.map(
      (assignment: AssignmentResponse) => ({
        application: WATERFORD_STUDENT,
        organization: assignment.organization,
        createItem: {
          metadata: assignment.metadata,
          school: assignment.school ?? schoolId,
          documentId: assignment.documentId,
          startDate: AssignmentTimes.IMMEDIATELY,
          endDate: AssignmentTimes.ON_COMPLETION,
          visibilityHours: assignment['visibilityHours'],
          visibilityExcludedDays: assignment['visibilityExcludedDays'],
          assignmentMethod: {
            method: Method.STANDALONE,
          },
        },
        user: assignment.user,
      }),
    );

    const deleteItems: AssignmentBatchItem[] = assignmentsToDelete.map(
      (assignment: AssignmentResponse) => ({
        application: WATERFORD_STUDENT,
        organization: assignment.organization,
        deleteItem: {
          assignmentId: assignment.id,
        },
        user: assignment.user,
      }),
    );

    return this.apiGatewayService
      .post<AssignmentBatchItemResponse[]>(
        'catalog-v5/v5/batch/assignments',
        [...createOrUpdateItems, ...deleteItems],
        'batchAssignments',
      )
      .pipe(
        tap((response) => {
          const failures = response
            .filter((item) => item.operationStatus !== 200)
            .map((item) => ({
              isLicenseError: item.operationMessage.includes(LicenseError.ACQUIRE_RELEASE_ERROR),
              message: item.operationMessage,
            }));
          if (throwError && !isEmpty(failures)) {
            throw new FailuresError(failures);
          }
        }),
        map((response) => {
          const successes = response
            .filter((item) => item.operationStatus === 200)
            .flatMap((item) => item.assignments || []);
          const failures = response.filter((item) => item.operationStatus !== 200);
          return { successes, failures };
        }),
      );
  }

  private getAssignments(
    organization: string,
    uuid: string,
    statuses = [AssignmentStatus.ACTIVE],
  ): Observable<AssignmentResponse[]> {
    return this.apiGatewayService.get<AssignmentResponse[]>(
      `catalog-v5/v5/apps/${WATERFORD_STUDENT}/orgs/${organization}/users/${uuid}/assignments/teacher?statuses=${statuses.toString()}`,
    );
  }
}
